import React, { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import BankReconciliationUpload from '@/components/BankReconciliationUpload';
import BankReconciliationDashboard from '@/components/BankReconciliationDashboard';
import OrphanResolverModal from '@/components/OrphanResolverModal';
import SubaccountSwitcher from '@/components/SubaccountSwitcher';
import { useBankReconciliation } from '@/hooks/useBankReconciliation';

const BankReconciliationPage = ({ onClose }) => {
  const {  user, activeAccountId } = useAuth();
  const { toast } = useToast();
  const {
    repassesData,
    extratoData,
    comparisonResult,
    loading,
    uploadRepasses,
    uploadExtrato,
    compareData,
    clearData,
    pendingOrphans,
    resolveOrphan
  } = useBankReconciliation();

  const [step, setStep] = useState('upload'); // 'upload' ou 'dashboard'
  const [agruparPorDia, setAgruparPorDia] = useState(false);

  const handleRepassesUpload = useCallback(async (file) => {
    try {
      await uploadRepasses(file, agruparPorDia);
      toast({
        title: "Sucesso",
        description: "Arquivo de repasses enviado com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao processar arquivo de repasses",
        variant: "destructive",
      });
    }
  }, [uploadRepasses, agruparPorDia, toast]);

  const handleExtratoUpload = useCallback(async (file) => {
    try {
      await uploadExtrato(file);
      toast({
        title: "Sucesso",
        description: "Arquivo de extrato enviado com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao processar arquivo de extrato",
        variant: "destructive",
      });
    }
  }, [uploadExtrato, toast]);

  const handleCompare = useCallback(async () => {
    if (!repassesData || repassesData.length === 0) {
      toast({
        title: "Atenção",
        description: "Por favor, envie o arquivo de repasses primeiro.",
        variant: "destructive",
      });
      return;
    }

    if (!extratoData || extratoData.length === 0) {
      toast({
        title: "Atenção",
        description: "Por favor, envie o arquivo de extrato primeiro.",
        variant: "destructive",
      });
      return;
    }

    try {
      await compareData();
      setStep('dashboard');
      toast({
        title: "Sucesso",
        description: "Comparação realizada com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao realizar comparação",
        variant: "destructive",
      });
    }
  }, [repassesData, extratoData, compareData, toast]);

  const handleReset = useCallback(() => {
    clearData();
    setStep('upload');
    setAgruparPorDia(false);
    toast({
      title: "Reset",
      description: "Dados limpos. Você pode fazer um novo upload.",
    });
  }, [clearData, toast]);

  return (
    <>
      <Helmet>
        <title>Conciliação Bancária - Conciliador de Cartões</title>
        <meta name="description" content="Concilie repasses com extratos bancários." />
      </Helmet>
      
      <div className="min-h-screen text-white p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-900 to-slate-800">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500">
                Conciliação Bancária
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <SubaccountSwitcher />
            </div>
          </header>

          {step === 'upload' ? (
            <div className="space-y-6">
              {/* Cards de Upload */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Upload Repasses */}
                <Card className="glass-effect border-white/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-green-400" />
                      Enviar Repasse
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <label className="flex items-center space-x-2 text-sm text-white/80 cursor-pointer p-2 rounded hover:bg-white/5 transition-colors border border-white/10">
                        <input 
                          type="checkbox" 
                          className="rounded border-white/20 bg-white/10 text-green-500 focus:ring-green-500/50 w-4 h-4 cursor-pointer"
                          checked={agruparPorDia} 
                          onChange={(e) => setAgruparPorDia(e.target.checked)} 
                        />
                        <span>Agrupar repasses por dia e tipo (Crédito/Débito)</span>
                      </label>
                    </div>
                    <BankReconciliationUpload
                      onUpload={handleRepassesUpload}
                      type="repasses"
                      loading={loading}
                      uploaded={repassesData && repassesData.length > 0}
                      count={repassesData?.length || 0}
                    />
                  </CardContent>
                </Card>

                {/* Upload Extrato */}
                <Card className="glass-effect border-white/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-blue-400" />
                      Enviar Extrato
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BankReconciliationUpload
                      onUpload={handleExtratoUpload}
                      type="extrato"
                      loading={loading}
                      uploaded={extratoData && extratoData.length > 0}
                      count={extratoData?.length || 0}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Botão de Comparação */}
              {repassesData && repassesData.length > 0 && extratoData && extratoData.length > 0 && (
                <div className="flex justify-center">
                  <Button
                    onClick={handleCompare}
                    disabled={loading}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 px-8 py-6 text-lg"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Realizar Comparação
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Informações */}
              <Card className="glass-effect border-white/20">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <div className="text-sm text-white/70">
                      <p className="font-semibold text-white mb-2">Instruções:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Envie o arquivo Excel (.xlsx ou .xls) com os repasses pagos</li>
                        <li>Envie o arquivo Excel (.xlsx ou .xls) com o extrato bancário</li>
                        <li>O sistema comparará valores e datas automaticamente</li>
                        <li>Após a comparação, você verá um dashboard com os resultados</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <BankReconciliationDashboard
              comparisonResult={comparisonResult}
              repassesData={repassesData}
              extratoData={extratoData}
              onReset={handleReset}
            />
          )}
        </div>
      </div>

      {/* Modal de Resolução de Taxas Órfãs iFood */}
      <OrphanResolverModal
        orphans={pendingOrphans}
        onResolve={(orphanId, lotId) => {
          resolveOrphan(orphanId, lotId);
          if (lotId) {
            toast({
              title: "Desconto aplicado",
              description: "A taxa foi abatida do lote selecionado.",
            });
          } else {
            toast({
              title: "Desconto não aplicado",
              description: "A cobrança avulsa foi ignorada.",
            });
          }
        }}
      />
    </>
  );
};

export default BankReconciliationPage;


